import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged } from 'rxjs';

import { CC_CONSTANTS } from '../../assets/constants';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorAec9Cream,
    colorAec10DarkGray,
} from 'src/app/shared/all.constants';

import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { GenericComponent } from '../generic/generic.component';
import { LegendConfig } from 'src/app/shared/components/map/map.component';

import { convertNumber } from '../../../../../shared/all.helpers';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
    MapColorsAlt,
} from 'src/app/shared/components/map/map.constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    CONTINUITY_QUALITY_SUPPLY_B_CRITERION_BREAKDOWN_CHART,
    CONTINUITY_QUALITY_SUPPLY_B_CRITERION_ENEDIS_INVESTMENT_CHART,
    CONTINUITY_QUALITY_SUPPLY_B_CRITERION_KPI,
    CONTINUITY_QUALITY_SUPPLY_HIX_B_CRITERION_MAP,
    CONTINUITY_QUALITY_SUPPLY_MEAN_HIX_B_CRITERION_MAP,
    CONTINUITY_QUALITY_SUPPLY_MEAN_TCC_B_CRITERION_MAP,
    CONTINUITY_QUALITY_SUPPLY_TCC_B_CRITERION_MAP,
} from '../../assets/keys';

const KEYS = [
    CONTINUITY_QUALITY_SUPPLY_B_CRITERION_KPI,
    CONTINUITY_QUALITY_SUPPLY_B_CRITERION_BREAKDOWN_CHART,
    CONTINUITY_QUALITY_SUPPLY_B_CRITERION_ENEDIS_INVESTMENT_CHART,
    CONTINUITY_QUALITY_SUPPLY_MEAN_TCC_B_CRITERION_MAP,
    CONTINUITY_QUALITY_SUPPLY_MEAN_HIX_B_CRITERION_MAP,
    CONTINUITY_QUALITY_SUPPLY_TCC_B_CRITERION_MAP,
    CONTINUITY_QUALITY_SUPPLY_HIX_B_CRITERION_MAP,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;
    tccBCriterion?: number;
    tccBCriterionEvolution?: number;
    hixBCriterion?: number;
    hixBCriterionEvolution?: number;
    incidentBCriterion?: number;
    incidentBCriterionEvolution?: number;
}

interface BreakdownChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: string;
    tccBCriterion?: number;
    totalHixBCriterion?: number;
    mvIncidents?: number;
    mvWorks?: number;
    lvIncidents?: number;
    lvWorks?: number;
    rteBCriterion?: number;
    psBCriterion?: number;
}

interface EnedisInvestmentChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: string;
    concessionInvestment?: number;
    concessionTotalHixBCriterion?: number;
    nationalInvestment?: number;
    nationalTotalHixBCriterion?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-supply-continuity-b-criterion',
    templateUrl: './supply-continuity-b-criterion.component.html',
    styleUrls: ['./supply-continuity-b-criterion.component.scss'],
})
export class SupplyContinuityBCriterionComponent extends GenericComponent {
    public texts = CC_CONSTANTS.continuityQuality.tabs.bCriterion.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_B_CRITERION_KPI),
    ) as Signal<KpiData[]>;

    public breakdownchartData: Signal<BreakdownChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_B_CRITERION_BREAKDOWN_CHART),
    ) as Signal<BreakdownChartData[]>;
    public breakdownChartSeries: ApexAxisChartSeries = [];
    public breakdownChartError: boolean = true;
    public breakdownDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([6]),
    };
    public breakdownXaxisCategories: ApexXAxis['categories'];
    public breakdownYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.BCriterionBreakdownChart.yaxisTitleLeft,
            },
        },
    ];

    public enedisInvestmentChartData: Signal<EnedisInvestmentChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(
            CONTINUITY_QUALITY_SUPPLY_B_CRITERION_ENEDIS_INVESTMENT_CHART,
        ),
    ) as Signal<EnedisInvestmentChartData[]>;
    public enedisInvestmentChartSeries: ApexAxisChartSeries = [];
    public enedisInvestmentChartError: boolean = true;
    public enedisInvestmentDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null, 3: null }),
    };
    public enedisInvestmentXaxisCategories: ApexXAxis['categories'];
    public enedisInvestmentYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.BCriterionEnedisInvestmentChart.series[0].name,
                this.texts.BCriterionEnedisInvestmentChart.series[1].name,
            ],
            min: 0,
            title: {
                text: this.texts.BCriterionEnedisInvestmentChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: [
                this.texts.BCriterionEnedisInvestmentChart.series[2].name,
                this.texts.BCriterionEnedisInvestmentChart.series[3].name,
            ],
            min: 0,
            opposite: true,
            title: {
                text: this.texts.BCriterionEnedisInvestmentChart.yaxisTitleRight,
            },
        },
    ];
    public enedisInvestmentGrid: ApexGrid = { padding: { left: 30, right: 30 } };
    public enedisInvestmentCustomTooltip: ApexTooltip['custom'];

    private defaultLegendValues = [
        { color: MapColorsAlt.darkGreen, value: '0 -  15min' },
        { color: MapColorsAlt.lightGreen, value: '15min - 30min' },
        { color: MapColorsAlt.lighterGreen, value: '30min - 1h' },
        { color: MapColorsAlt.paleGreen, value: '1h - 2h' },
        { color: colorAec4Orange, value: '2h - 3h' },
        { color: colorAec5Brown, value: '3h - 4h' },
        { color: colorAec8RedDark, value: '4h+' },
        { color: `url(${HatchImage})`, value: 'Communes hors concession' },
    ];
    public meanTccGeometriesSignal: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_MEAN_TCC_B_CRITERION_MAP),
    ) as Signal<any[]>;
    public meanTccLegendConfig: LegendConfig[] = [
        {
            title: 'Critère B TCC moyen',
            values: this.defaultLegendValues,
        },
    ];

    public meanHixGeometriesSignal: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_MEAN_HIX_B_CRITERION_MAP),
    ) as Signal<any[]>;
    public meanHixLegendConfig: LegendConfig[] = [
        {
            title: 'Critère B sur incidents HIX moyen',
            values: this.defaultLegendValues,
        },
    ];

    public tccGeometriesSignal: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_TCC_B_CRITERION_MAP),
    ) as Signal<any[]>;
    public tccLegendConfig: LegendConfig[] = [
        {
            title: 'Critère B TCC',
            values: this.defaultLegendValues,
        },
    ];

    public hixGeometriesSignal: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_HIX_B_CRITERION_MAP),
    ) as Signal<any[]>;
    public hixLegendConfig: LegendConfig[] = [
        {
            title: 'Critère B sur incidents HIX',
            values: this.defaultLegendValues,
        },
    ];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const breakdownchartData = this.breakdownchartData();
            this.breakdownChartError = this.checkForError(breakdownchartData, [
                'xYear',
                'tccBCriterion',
                'totalHixBCriterion',
                'mvIncidents',
                'mvWorks',
                'lvIncidents',
                'lvWorks',
                'rteBCriterion',
                'psBCriterion',
            ]);
            if (!this.breakdownChartError) {
                this.breakdownXaxisCategories = breakdownchartData.map((data) => data.xYear);
                this.breakdownChartSeries = [
                    {
                        name: this.texts.BCriterionBreakdownChart.series[0].name,
                        type: 'column',
                        data: breakdownchartData.map((data) => data.mvIncidents!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.BCriterionBreakdownChart.series[1].name,
                        type: 'column',
                        data: breakdownchartData.map((data) => data.lvIncidents!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.BCriterionBreakdownChart.series[2].name,
                        type: 'column',
                        data: breakdownchartData.map((data) => data.mvWorks!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.BCriterionBreakdownChart.series[3].name,
                        type: 'column',
                        data: breakdownchartData.map((data) => data.lvWorks!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.BCriterionBreakdownChart.series[4].name,
                        type: 'column',
                        data: breakdownchartData.map((data) => data.rteBCriterion!),
                        color: colorAec10DarkGray,
                    },
                    {
                        name: this.texts.BCriterionBreakdownChart.series[5].name,
                        type: 'column',
                        data: breakdownchartData.map((data) => data.psBCriterion!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.BCriterionBreakdownChart.series[6].name,
                        type: 'line',
                        data: breakdownchartData.map((data) => data.totalHixBCriterion!),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.BCriterionBreakdownChart.series[7].name,
                        type: 'line',
                        data: breakdownchartData.map((data) => data.tccBCriterion!),
                        color: colorAec8RedDark,
                    },
                ];
                this.breakdownDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5, 6, 7];
            }

            const enedisInvestmentChartData = this.enedisInvestmentChartData();
            this.enedisInvestmentChartError = this.checkForError(enedisInvestmentChartData, [
                'xYear',
                'concessionInvestment',
                'concessionTotalHixBCriterion',
                'nationalInvestment',
                'nationalTotalHixBCriterion',
            ]);
            if (!this.enedisInvestmentChartError) {
                this.enedisInvestmentXaxisCategories = enedisInvestmentChartData.map(
                    (data) => data.xYear,
                );
                this.enedisInvestmentChartSeries = [
                    {
                        name: this.texts.BCriterionEnedisInvestmentChart.series[0].name,
                        type: 'line',
                        data: enedisInvestmentChartData.map((data) =>
                            Math.round(data.concessionTotalHixBCriterion!),
                        ),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.BCriterionEnedisInvestmentChart.series[1].name,
                        type: 'line',
                        data: enedisInvestmentChartData.map((data) =>
                            Math.round(data.nationalTotalHixBCriterion!),
                        ),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.BCriterionEnedisInvestmentChart.series[2].name,
                        type: 'line',
                        data: enedisInvestmentChartData.map((data) =>
                            Math.round(data.concessionInvestment!),
                        ),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.BCriterionEnedisInvestmentChart.series[3].name,
                        type: 'line',
                        data: enedisInvestmentChartData.map((data) =>
                            Math.round(data.nationalInvestment!),
                        ),
                        color: colorAec1BlueLight,
                    },
                ];
                this.enedisInvestmentDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(([currentOrganization, currentYear]) => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));

                const meanTccBCriteriontitle = this.texts.meanTccBCriterionMap.title;
                this.texts.meanTccBCriterionMap.title = this.setMapTitle(
                    meanTccBCriteriontitle,
                    currentYear,
                );

                const meanHixBCriteriontitle = this.texts.meanHixBCriterionMap.title;
                this.texts.meanHixBCriterionMap.title = this.setMapTitle(
                    meanHixBCriteriontitle,
                    currentYear,
                );

                const tccBCriteriontitle = this.texts.tccBCriterionMap.title;
                this.texts.tccBCriterionMap.title = this.setMapTitle(
                    tccBCriteriontitle,
                    currentYear,
                );

                const hixBCriteriontitle = this.texts.hixBCriterionMap.title;
                this.texts.hixBCriterionMap.title = this.setMapTitle(
                    hixBCriteriontitle,
                    currentYear,
                );
            });

        this.enedisInvestmentCustomTooltip = this.setEnedisInvestmentCustomTooltip();
    }

    private setEnedisInvestmentCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const ccCriterionPrecision =
                this.texts.BCriterionEnedisInvestmentChart.series[0].precision;
            const ccCriterionSerie = w.globals.initialSeries[0];
            const {
                name: ccCriterionName,
                color: ccCriterionColor,
                valueStringified: ccCriterionStringified,
            } = this.setCustomTooltipData(ccCriterionPrecision, ccCriterionSerie, dataPointIndex);

            const nationalCriterionPrecision =
                this.texts.BCriterionEnedisInvestmentChart.series[1].precision;
            const nationalCriterionSerie = w.globals.initialSeries[1];
            const {
                name: nationalCriterionName,
                color: nationalCriterionColor,
                valueStringified: nationalCriterionStringified,
            } = this.setCustomTooltipData(
                nationalCriterionPrecision,
                nationalCriterionSerie,
                dataPointIndex,
            );

            const ccInvestPrecision =
                this.texts.BCriterionEnedisInvestmentChart.series[2].precision;
            const ccInvestSerie = w.globals.initialSeries[2];
            const {
                name: ccInvestName,
                color: ccInvestColor,
                valueStringified: ccInvestStringified,
            } = this.setCustomTooltipData(ccInvestPrecision, ccInvestSerie, dataPointIndex);

            const nationalInvestPrecision =
                this.texts.BCriterionEnedisInvestmentChart.series[3].precision;
            const nationalInvestSerie = w.globals.initialSeries[3];
            const {
                name: nationalInvestName,
                color: nationalInvestColor,
                valueStringified: nationalInvestStringified,
            } = this.setCustomTooltipData(
                nationalInvestPrecision,
                nationalInvestSerie,
                dataPointIndex,
            );

            const dataTooltips = [
                {
                    color: ccCriterionColor,
                    name: ccCriterionName,
                    value: ccCriterionStringified + ' min.',
                },
                {
                    color: nationalCriterionColor,
                    name: nationalCriterionName,
                    value: nationalCriterionStringified + ' min.',
                },
                {
                    color: ccInvestColor,
                    name: ccInvestName,
                    value: ccInvestStringified + ' €/us.',
                },
                {
                    color: nationalInvestColor,
                    name: nationalInvestName,
                    value: nationalInvestStringified + ' €/us.',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }
    private setMapTitle(rawTitle: string, currentYear: number): string {
        return rawTitle
            .replace('N-2', (currentYear - 2).toString())
            .replace('N', currentYear.toString());
    }

    public setMapStyle(kpi: string) {
        return function (feature: GeoJSON.Feature | undefined) {
            if (!feature?.properties || feature.properties[kpi] === null) {
                return EmptyPolygonStyle;
            }

            let fillColor = MapColorsAlt.darkGreen;

            const meanTccBCriterion = feature.properties[kpi];
            if (meanTccBCriterion > 4 * 60) {
                fillColor = colorAec8RedDark;
            } else if (meanTccBCriterion > 3 * 60) {
                fillColor = colorAec5Brown;
            } else if (meanTccBCriterion > 2 * 60) {
                fillColor = colorAec4Orange;
            } else if (meanTccBCriterion > 60) {
                fillColor = MapColorsAlt.paleGreen;
            } else if (meanTccBCriterion > 30) {
                fillColor = MapColorsAlt.lighterGreen;
            } else if (meanTccBCriterion > 15) {
                fillColor = MapColorsAlt.lightGreen;
            }

            return {
                ...DefaultPolygonStyle,
                fillColor,
            };
        };
    }

    public setTooltip(kpi: string) {
        return function (feature: GeoJSON.Feature) {
            if (!feature.properties) {
                return '';
            }
            const cityName = feature.properties['cityName'];
            const value = feature.properties[kpi];
            const isInConcession = feature.properties['isInConcession'];

            if (!isInConcession) {
                return `${cityName} - Hors concession`;
            }

            const valueStringified = convertNumber(Number(value));
            let unit = 'minute';
            unit = value > 1 ? unit + 's' : unit;
            return `${cityName} - ${valueStringified} ${unit}`;
        };
    }
}
